import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import appStore from "../../storage/AppStore";
import systemStore from "../../storage/SystemStore";

import Btn from "../../ui/Btn";
import AwardEditor from "./AwardEditor";
import "./../../common/game.css";

const AwardsList = ({
  role = "MGR",
  scope = "MATCH",
  awardStore,
  context = {},
  sport_id = null,
  teams = [],
  individualPlayers = [],
  filters = {},
  autoLoad = true,
  className = "",
}) => {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const canEdit = role === "MGR" || role === "TRN";
  const language = appStore.user?.language || "";

  const contextKey = useMemo(
    () =>
      JSON.stringify({
        company_id: context.company_id ?? null,
        tournament_id: context.tournament_id ?? null,
        game_id: context.game_id ?? null,
        team_class_id: context.team_class_id ?? null,
        scope,
        sport_id: sport_id ?? null,
        filters,
      }),
    [
      context.company_id,
      context.tournament_id,
      context.game_id,
      context.team_class_id,
      scope,
      sport_id,
      filters,
    ]
  );

  const listPayload = useMemo(
    () => ({
      ...filters,
      company_id: context.company_id ?? undefined,
      tournament_id: context.tournament_id ?? undefined,
      game_id: context.game_id ?? undefined,
      team_class_id: context.team_class_id ?? undefined,
      scope,
      sport_id: sport_id ?? undefined,
      include_common: true,
    }),
    [
      filters,
      context.company_id,
      context.tournament_id,
      context.game_id,
      context.team_class_id,
      scope,
      sport_id,
    ]
  );

  const reload = async () => {
    if (!awardStore) return;

    awardStore.setContext({
      company_id: context.company_id ?? null,
      tournament_id: context.tournament_id ?? null,
      game_id: context.game_id ?? null,
      team_class_id: context.team_class_id ?? null,
    });

    awardStore.setTypeFilter({
      sport_id: sport_id ?? null,
      scope,
      include_common: true,
    });

    await awardStore.getAwardTypes().catch(console.error);
    await awardStore.listAwards(listPayload).catch(console.error);
  };

  useEffect(() => {
    if (!autoLoad || !awardStore) return;
    reload().catch(console.error);
  }, [autoLoad, awardStore, contextKey, language]);

  const onSaved = async () => {
    setEditing(null);
    setCreating(false);
    await reload();
  };

  const onDelete = async (a) => {
    if (!canEdit) return;
    if (window.confirm(appStore.tr.are_you_sure_delete)) {
      await awardStore.deleteAward({ id: a.id }).catch(console.error);
      await reload();
    }
  };

  return (
    <div className={className}>
      <div className="flex game-page justify-content-between align-items-center mb-2">
        <div className="font-bold">{appStore.tr.awards}</div>

        <div className="flex gap-2">
          <Btn
            icon="pi pi-refresh"
            outlined
            severity="secondary"
            onClick={() => reload()}
          />

          {canEdit && (
            <Btn
              icon="pi pi-plus"
              severity="success"
              onClick={() => {
                setCreating(true);
                setEditing(null);
              }}
            />
          )}
        </div>
      </div>

      {creating && (
        <div className="mb-3 game-page">
          <AwardEditor
            role={role}
            scope={scope}
            awardStore={awardStore}
            award={null}
            context={context}
            sport_id={sport_id}
            teams={teams}
            individualPlayers={individualPlayers}
            onSaved={onSaved}
            onCancel={() => setCreating(false)}
          />
        </div>
      )}

      {editing && (
        <div className="mb-3">
          <AwardEditor
            role={role}
            scope={scope}
            awardStore={awardStore}
            award={editing}
            context={context}
            sport_id={sport_id}
            teams={teams}
            individualPlayers={individualPlayers}
            onSaved={onSaved}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      {systemStore.loading ? (
        <div className="p-2">{appStore.tr.loading}</div>
      ) : (
        <div className="flex flex-column gap-2 mb-5">
          {(awardStore?.awards || []).map((a) => (
            <div
              key={a.id}
              className="game-page award-item flex justify-content-between align-items-center"
            >
              <div className="award-icon">
                {a.company_user_id > 0 && (
                  <img
                    src={a.company_user.avatar}
                    alt="Member Photo"
                    className="w-full border-round"
                  />
                )}
              </div>

              <div className="award-content">
                <div className="award-title">
                  {a.award_type_name || a.name || a.award_type_id}
                </div>

                <div className="award-details">
                  {a.company_user_id > 0 && a.company_user?.title}
                  {a.team_id > 0 && ` • ${a.company_team?.name}`}
                  {a.team_class_id > 0 && ` • ${a.team_class?.class_date}`}
                  {a.game_team_id > 0 && ` • ${a.game_team?.name}`}
                  {a.game_id > 0 && ` • ${a.game?.name}`}
                  {a.tournament_id > 0 && ` • ${a.tournament?.name}`}
                  {a.name ? ` • ${a.name}` : ""}
                </div>
              </div>

              {canEdit && (
                <div className="award-actions">
                  <Btn
                    icon="pi pi-pencil"
                    outlined
                    onClick={() => {
                      setEditing(a);
                      setCreating(false);
                    }}
                  />
                  <Btn
                    icon="pi pi-trash"
                    severity="danger"
                    outlined
                    onClick={() => onDelete(a)}
                  />
                </div>
              )}
            </div>
          ))}

          {(awardStore?.awards || []).length === 0 && (
            <div className="p-2 opacity-70">{appStore.tr.no_awards_yet}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default observer(AwardsList);